/* src/App.js - Última Modificación: 28-02-2026 */
/* -------------------------------------------- */

import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  useMediaQuery,
  Box
} from "@mui/material";

import Navbar from "./components/Navbar.jsx";
import Sidebar from "./components/Sidebar.jsx";
import MainContent from "./components/MainContent.jsx";
import Login from "./components/pages/Login.jsx";

import { FeedbackProvider } from "./context/FeedbackContext.jsx";
import { MainContentProvider } from "./context/MainContentContext.jsx";
import { useAuth } from "./context/AuthContext.jsx";

import SessionWarningDialog from "./components/session/SessionWarningDialog.jsx";
import { getCustomTheme } from "./theme/customTheme.js";

const App = () => {
  /* 🌗 MODO CLARO / OSCURO */
  const [modo, setModo] = useState(
    localStorage.getItem("modo") || "light"
  );

  const darkTheme = useMemo(() => {
    const baseTheme = createTheme();
    return createTheme(getCustomTheme(baseTheme, "dark"));
  }, []);

  const theme = useMemo(() => {
    const baseTheme = createTheme();
    return createTheme(getCustomTheme(baseTheme, modo));
  }, [modo]);

  useEffect(() => {
    localStorage.setItem("modo", modo);
  }, [modo]);

  /* 🔐 AUTH */
  const { user } = useAuth();

  /* 📂 SIDEBAR */
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [selectedMenu, setSelectedMenu] = useState("CLIENTES");

  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  const toggleSidebar = (value) => {
    if (typeof value === "boolean") {
      setIsSidebarOpen(value);
    } else {
      setIsSidebarOpen((prev) => !prev);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <FeedbackProvider>
        <Routes>
          {/* 🔓 LOGIN */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/home" replace />
              ) : (
                <ThemeProvider theme={darkTheme}>
                  <CssBaseline />
                  <Login />
                </ThemeProvider>
              )
            }
          />

          {/* 🔒 APP PRIVADA */}
          <Route
            path="/*"
            element={
              user ? (
                <>
                  {/* ⚠️ ÚNICO gestor visual de sesión */}
                  <SessionWarningDialog />

                  <Box
                    sx={{
                      height: "100vh",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Navbar
                      toggleSidebar={toggleSidebar}
                      modo={modo}
                      setModo={setModo}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Sidebar
                        isSidebarOpen={isSidebarOpen}
                        selectedMenu={selectedMenu}
                        toggleSidebar={toggleSidebar}
                      />

                      <Box
                        component="main"
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          minWidth: 0,
                          overflow: "hidden",
                        }}
                      >
                        <MainContentProvider>
                          <MainContent onMenuSelect={setSelectedMenu} />
                        </MainContentProvider>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </FeedbackProvider>
    </ThemeProvider>
  );
};

export default App;
